/**
 * ToolTypeIcon — Unified icon component for tool types.
 *
 * Maps tool types to semantic lucide-react icons. Replaces text-only badges
 * with a consistent visual language across the app.
 *
 * Stakeholder feedback wave 1-2: Unified Icon Design Language
 */

import { cva, type VariantProps } from 'class-variance-authority';
import {
  ArrowRightLeft,
  CheckCircle,
  Eye,
  FileCode,
  PenLine,
  Sparkles,
} from 'lucide-react';
import React from 'react';

import { cn } from '@/lib/utils';

export interface ToolTypeIconProps extends VariantProps<typeof toolTypeIconVariants> {
  /** The tool type (transformer, generator, visualizer, etc.) */
  type: string;
  /** Show text label alongside icon */
  showLabel?: boolean;
  className?: string;
}

const TOOL_TYPE_CONFIG: Record<
  string,
  {
    icon: React.ComponentType<{ className?: string }>;
    label: string;
    colorClass: string;
  }
> = {
  transformer: {
    icon: ArrowRightLeft,
    label: 'Transform',
    colorClass: 'text-primary bg-primary/10',
  },
  generator: {
    icon: Sparkles,
    label: 'Generate',
    colorClass: 'text-accent-foreground bg-accent',
  },
  visualizer: {
    icon: Eye,
    label: 'View',
    colorClass: 'text-muted-foreground bg-muted',
  },
  editor: {
    icon: PenLine,
    label: 'Edit',
    colorClass: 'text-primary bg-primary/10',
  },
  validator: {
    icon: CheckCircle,
    label: 'Validate',
    colorClass: 'text-secondary-foreground bg-secondary',
  },
  encoder: {
    icon: FileCode,
    label: 'Encode',
    colorClass: 'text-muted-foreground bg-muted/80',
  },
};

const toolTypeIconVariants = cva(
  'inline-flex items-center gap-1 rounded font-medium shrink-0',
  {
    variants: {
      size: {
        sm: 'px-1 py-0.5 text-[9px]',
        md: 'px-1.5 py-0.5 text-[10px]',
        lg: 'px-2 py-1 text-xs',
      },
    },
    defaultVariants: {
      size: 'md',
    },
  }
);

export const ToolTypeIcon: React.FC<ToolTypeIconProps> = ({
  type,
  showLabel = true,
  size,
  className,
}) => {
  const config = TOOL_TYPE_CONFIG[type] ?? {
    icon: FileCode,
    label: type,
    colorClass: 'text-muted-foreground bg-muted',
  };

  const Icon = config.icon;
  const iconSize = size === 'sm' ? 'h-2.5 w-2.5' : size === 'lg' ? 'h-3.5 w-3.5' : 'h-3 w-3';

  return (
    <span
      className={cn(
        toolTypeIconVariants({ size }),
        config.colorClass,
        className
      )}
      aria-label={`${config.label} tool`}
    >
      <Icon className={iconSize} />
      {showLabel && <span>{config.label}</span>}
    </span>
  );
};
